import { useQuery } from "@tanstack/react-query";
import { cn } from "@tcg/ui";
import { FileSignature, ShieldAlert, ShieldCheck, UserPlus, UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCan, usePos } from "@/app/context";
import { call } from "@/lib/api";
import type { ConsignorBrief, ConsignorCreate } from "@/lib/consignment-types";
import { errorText } from "@/lib/errors";
import { newIdempotencyKey } from "@/lib/idempotency";

export interface ChosenConsignor {
  id: string;
  reference: string;
  name: string;
  status: string;
  identityVerified: boolean;
  agreementAccepted: boolean;
  /** 本人確認 made at this counter just now (sent with the intake). */
  identityCheckId?: string | null;
  /** 氏名（書類どおり） typed at this counter when the consignor was created here — the 本人確認
   * form starts from it. Never the display name: the 古物台帳 must carry the name on the
   * document, not how the shop calls the person (T13-29 UAT). Unknown (masked) → empty. */
  legalName?: string | null;
}

export function fromBrief(brief: ConsignorBrief): ChosenConsignor {
  return {
    id: brief.id,
    reference: brief.reference,
    name: brief.displayName,
    status: brief.status,
    identityVerified: brief.identityVerified,
    agreementAccepted: brief.agreementAccepted,
  };
}

/** The 振込先 typed at the counter for a new consignor paid by bank transfer. */
export interface NewBankAccount {
  bankName: string;
  branchCode: string;
  accountType: "普通" | "当座";
  accountNumber: string;
  holderKana: string;
}

const EMPTY_BANK: NewBankAccount = {
  bankName: "",
  branchCode: "",
  accountType: "普通",
  accountNumber: "",
  holderKana: "",
};

/** Every box filled (consignment-service normalises full-width digits / half-width kana). */
function bankAccountComplete(bank: NewBankAccount): boolean {
  return [bank.bankName, bank.branchCode, bank.accountNumber, bank.holderKana].every(
    (v) => v.trim() !== "",
  );
}

function bankAccountBody(bank: NewBankAccount): NonNullable<ConsignorCreate["bankAccount"]> {
  return {
    bankName: bank.bankName.trim(),
    branchCode: bank.branchCode.trim(),
    accountType: bank.accountType,
    accountNumber: bank.accountNumber.trim(),
    holderKana: bank.holderKana.trim(),
  };
}

/** What still stops a 委託受付 for this consignor (one sentence each, before anything is sent). */
export function consignorBlockers(
  c: ChosenConsignor,
): Array<"identity" | "agreement" | "suspended"> {
  const out: Array<"identity" | "agreement" | "suspended"> = [];
  if (c.status === "suspended" || c.status === "closed") {
    out.push("suspended");
  }
  if (!c.identityVerified) {
    out.push("identity");
  }
  if (!c.agreementAccepted) {
    out.push("agreement");
  }
  return out;
}

/**
 * The consignor (T07-32, design/05 §3.5): find by reference (CON-…) / name / phone (masked answers,
 * at most 10), or 「+新規」 (a manager — `consignment.manage`: the consignor record, then 本人確認
 * and the signed agreement at the counter). Shows whether 本人確認 is valid and the agreement signed;
 * without them the intake cannot start.
 */
export function ConsignorSearch({ onChoose }: { onChoose: (c: ChosenConsignor) => void }) {
  const { t } = useTranslation();
  const { consignment } = usePos();
  const canCreate = useCan("consignment.manage");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const term = q.trim();
  const found = useQuery({
    queryKey: ["pos-consignors", "list", term],
    queryFn: () =>
      call(() => consignment.GET("/v1/pos/consignors", { params: { query: { q: term } } })),
    enabled: term.length >= 2,
    staleTime: 10_000,
  });

  if (creating) {
    return (
      <NewConsignorForm
        onCancel={() => setCreating(false)}
        onCreated={(c) => {
          setCreating(false);
          onChoose(c);
        }}
      />
    );
  }

  return (
    <section
      aria-labelledby="consignor-title"
      className="flex flex-col gap-3 rounded-lg border p-4"
    >
      <h2 id="consignor-title" className="text-xl font-semibold">
        {t("consign.consignor_title")}
      </h2>
      <div className="flex flex-wrap gap-2">
        <label className="flex min-w-0 flex-1 flex-col">
          <span className="sr-only">{t("consign.search")}</span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("consign.search_placeholder")}
            autoComplete="off"
            className="min-h-pos-touch rounded-md border px-3 text-lg"
          />
        </label>
        <button
          type="button"
          onClick={() => setCreating(true)}
          disabled={!canCreate}
          aria-describedby={canCreate ? undefined : "consignor-new-why"}
          className="flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg disabled:opacity-50"
        >
          <UserPlus aria-hidden className="size-5" />
          {t("consign.new")}
        </button>
      </div>
      {canCreate ? null : (
        <p id="consignor-new-why" className="text-sm text-muted-foreground">
          {t("consign.new_manager_only")}
        </p>
      )}
      <ul className="flex flex-col gap-1">
        {(found.data?.data ?? []).map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onChoose(fromBrief(c))}
              className="flex min-h-pos-touch w-full flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-left"
            >
              <UserRound aria-hidden className="size-5" />
              <strong>{c.reference}</strong>
              <span className="text-lg">{c.displayName}</span>
              {c.legalNameMasked ? (
                <span className="text-muted-foreground">{c.legalNameMasked}</span>
              ) : null}
              {c.phoneLast4 ? <span className="text-muted-foreground">…{c.phoneLast4}</span> : null}
              <span
                className={cn(
                  "ml-auto text-sm",
                  c.identityVerified ? "text-pos-ok" : "text-pos-problem",
                )}
              >
                {c.identityVerified ? t("consign.identity_ok") : t("consign.identity_needed")}
              </span>
            </button>
          </li>
        ))}
        {term.length >= 2 && found.data && found.data.data.length === 0 ? (
          <li className="text-muted-foreground">{t("consign.none_found")}</li>
        ) : null}
      </ul>
    </section>
  );
}

function NewConsignorForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (c: ChosenConsignor) => void;
}) {
  const { t, i18n } = useTranslation();
  const { consignment } = usePos();
  const [name, setName] = useState("");
  const [legal, setLegal] = useState("");
  const [kana, setKana] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [payout, setPayout] = useState<ConsignorCreate["payoutMethod"]>("cash");
  // 振込: consignment-service needs the account in the same request (`bank_account_required`
  // otherwise — T08-02); it is stored encrypted and only ever shown masked (T13-07)
  const [bank, setBank] = useState<NewBankAccount>(EMPTY_BANK);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsBank = payout === "bank_transfer";
  const bankReady = !needsBank || bankAccountComplete(bank);

  const create = async () => {
    if (!name.trim() || !bankReady) {
      return;
    }
    setBusy(true);
    setError(null);
    const locale =
      (["en", "ja", "th", "zh-Hant", "zh-Hans"] as const).find(
        (l) => l === i18n.resolvedLanguage,
      ) ?? "ja";
    try {
      const created = await call(() =>
        consignment.POST("/v1/admin/consignors", {
          body: {
            displayName: name.trim(),
            ...(legal.trim() ? { legalName: legal.trim() } : {}),
            ...(kana.trim() ? { nameKana: kana.trim() } : {}),
            ...(phone.trim() ? { phone: phone.trim() } : {}),
            ...(email.trim() ? { email: email.trim() } : {}),
            locale,
            payoutMethod: payout,
            ...(needsBank ? { bankAccount: bankAccountBody(bank) } : {}),
            // consignment-service's defaults (the terms are changed there, not at the counter)
            paymentFeePolicy: "shop_absorbs",
            payoutSchedule: "monthly",
            invoiceRegistered: false,
          },
          params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        }),
      );
      onCreated({
        id: created.id,
        reference: created.reference,
        name: created.displayName,
        legalName: legal.trim() || null,
        status: created.status,
        identityVerified: false,
        agreementAccepted: false,
      });
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const input = (
    label: string,
    value: string,
    set: (v: string) => void,
    extra: Record<string, unknown> = {},
  ) => (
    <label className="flex flex-col gap-1">
      <span className="font-medium">{label}</span>
      <input
        value={value}
        onChange={(e) => set(e.target.value)}
        autoComplete="off"
        className="min-h-pos-touch rounded-md border px-3 text-lg"
        {...extra}
      />
    </label>
  );

  return (
    <section aria-labelledby="new-consignor" className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 id="new-consignor" className="text-xl font-semibold">
        {t("consign.new_title")}
      </h2>
      <div className="grid gap-3 md:grid-cols-2">
        {input(t("consign.display_name"), name, setName)}
        {input(t("consign.legal_name"), legal, setLegal)}
        {input(t("consign.name_kana"), kana, setKana)}
        {input(t("customer.phone"), phone, setPhone, { inputMode: "tel" })}
        {input(t("customer.email"), email, setEmail, { type: "email" })}
        <fieldset className="flex flex-col gap-1">
          <legend className="font-medium">{t("consign.payout")}</legend>
          <div className="flex flex-wrap gap-2">
            {(["cash", "bank_transfer", "store_credit"] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={payout === m}
                onClick={() => setPayout(m)}
                className={cn(
                  "min-h-pos-touch rounded-md border px-4 text-lg",
                  payout === m && "border-primary bg-primary text-primary-foreground",
                )}
              >
                {t(`consign.payout_${m}`)}
              </button>
            ))}
          </div>
        </fieldset>
      </div>
      {needsBank ? (
        <fieldset className="flex flex-col gap-3 rounded-md border p-3">
          <legend className="px-1 font-medium">{t("consign.bank_title")}</legend>
          <span className="text-sm text-muted-foreground">{t("consign.bank_hint")}</span>
          <div className="grid gap-3 md:grid-cols-2">
            {input(t("consign.bank_name"), bank.bankName, (v) => setBank({ ...bank, bankName: v }))}
            {input(
              t("consign.bank_branch"),
              bank.branchCode,
              (v) => setBank({ ...bank, branchCode: v }),
              { inputMode: "numeric" },
            )}
            <div role="group" aria-label={t("consign.bank_type")} className="flex flex-col gap-1">
              <span className="font-medium">{t("consign.bank_type")}</span>
              <div className="flex gap-2">
                {(["普通", "当座"] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    aria-pressed={bank.accountType === kind}
                    onClick={() => setBank({ ...bank, accountType: kind })}
                    className={cn(
                      "min-h-pos-touch rounded-md border px-4 text-lg",
                      bank.accountType === kind &&
                        "border-primary bg-primary text-primary-foreground",
                    )}
                  >
                    {t(kind === "普通" ? "consign.bank_type_futsu" : "consign.bank_type_toza")}
                  </button>
                ))}
              </div>
            </div>
            {input(
              t("consign.bank_number"),
              bank.accountNumber,
              (v) => setBank({ ...bank, accountNumber: v }),
              { inputMode: "numeric" },
            )}
            {input(t("consign.bank_holder"), bank.holderKana, (v) =>
              setBank({ ...bank, holderKana: v }),
            )}
          </div>
        </fieldset>
      ) : null}
      {error ? (
        <p role="alert" className="text-pos-problem">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="min-h-pos-touch rounded-md border px-6 text-lg"
        >
          {t("app.cancel")}
        </button>
        <button
          type="button"
          onClick={() => void create()}
          disabled={busy || !name.trim() || !bankReady}
          className="min-h-pos-touch rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground disabled:opacity-50"
        >
          {t("consign.create")}
        </button>
      </div>
    </section>
  );
}

/** The chosen consignor + what is still missing (本人確認 / agreement) with the buttons to fix it. */
export function ConsignorSummary({
  consignor,
  onIdentity,
  onAgreement,
  onChange,
  busy,
}: {
  consignor: ChosenConsignor;
  onIdentity: () => void;
  onAgreement: () => void;
  onChange: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const canManage = useCan("consignment.manage");
  const blockers = consignorBlockers(consignor);
  return (
    <section
      aria-label={t("consign.consignor_title")}
      className="flex flex-col gap-2 rounded-lg border p-3"
    >
      <div className="flex flex-wrap items-center gap-3">
        <UserRound aria-hidden className="size-6" />
        <strong className="text-lg">{consignor.reference}</strong>
        <span className="text-lg">{consignor.name}</span>
        <button
          type="button"
          onClick={onChange}
          className="ml-auto min-h-12 rounded-md border px-4"
        >
          {t("consign.change")}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        <span
          role="status"
          className={cn(
            "flex items-center gap-1 rounded-full px-3 py-1 font-semibold text-white",
            consignor.identityVerified ? "bg-pos-ok" : "bg-status-problem",
          )}
        >
          {consignor.identityVerified ? (
            <ShieldCheck aria-hidden className="size-5" />
          ) : (
            <ShieldAlert aria-hidden className="size-5" />
          )}
          {consignor.identityVerified ? t("consign.identity_ok") : t("consign.identity_needed")}
        </span>
        <span
          className={cn(
            "flex items-center gap-1 rounded-full px-3 py-1 font-semibold text-white",
            consignor.agreementAccepted ? "bg-pos-ok" : "bg-status-problem",
          )}
        >
          <FileSignature aria-hidden className="size-5" />
          {consignor.agreementAccepted ? t("consign.agreement_ok") : t("consign.agreement_needed")}
        </span>
      </div>
      {blockers.includes("suspended") ? (
        <p className="text-pos-problem">{t("consign.blocker_suspended")}</p>
      ) : null}
      {blockers.includes("identity") ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-pos-problem">{t("consign.blocker_identity")}</p>
          <button
            type="button"
            onClick={onIdentity}
            disabled={busy}
            className="min-h-pos-touch rounded-md bg-primary px-5 text-lg font-semibold text-primary-foreground disabled:opacity-50"
          >
            {t("buylist.identity_check")}
          </button>
        </div>
      ) : null}
      {blockers.includes("agreement") ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-pos-problem">
            {canManage ? t("consign.blocker_agreement") : t("consign.blocker_agreement_manager")}
          </p>
          {canManage ? (
            <button
              type="button"
              onClick={onAgreement}
              disabled={busy}
              className="min-h-pos-touch rounded-md border px-5 text-lg disabled:opacity-50"
            >
              {t("consign.agreement_signed")}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
